import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { CodingChallenge } from '../types';

interface CodingChallengeCardProps {
  challenge: CodingChallenge;
}

const CodingChallengeCard = ({ challenge }: CodingChallengeCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy':
        return 'var(--color-success)';
      case 'Medium':
        return 'var(--color-warning)';
      case 'Hard':
        return 'var(--color-destructive)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  return (
    <div className="bg-surface rounded-lg p-6 border border-border hover:border-primary/50 transition-all duration-300">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name={challenge.icon} size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">{challenge.title}</h3>
            <p className="text-sm text-muted-foreground">{challenge.language}</p>
          </div>
        </div>
        {challenge.completed && (
          <div className="flex items-center gap-2 px-3 py-1 bg-success/10 rounded-full">
            <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
            <span className="text-sm text-success">Completed</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div
          className="px-3 py-1 rounded-full text-sm font-medium"
          style={{
            backgroundColor: `${getDifficultyColor(challenge.difficulty)}20`,
            color: getDifficultyColor(challenge.difficulty),
          }}
        >
          {challenge.difficulty}
        </div>
      </div>

      <p className={`text-sm text-muted-foreground mb-4 ${!isExpanded && 'line-clamp-2'}`}>
        {challenge.description}
      </p>

      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
          iconPosition="right"
        >
          {isExpanded ? 'Show Less' : 'Show More'}
        </Button>
        {!challenge.completed && (
          <Button variant="default" size="sm" iconName="Play" iconPosition="left">
            Try Challenge
          </Button>
        )}
      </div>
    </div>
  );
};

export default CodingChallengeCard;